"use client";

import React from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { SortingState } from '@tanstack/react-table';

/**
 * Sort options offered next to the programme search box. `columnId` matches the
 * TanStack column id so the table view's header sorting and these buttons share
 * one piece of state.
 */
export const PROGRAMME_SORT_OPTIONS = [
    { columnId: 'jupas_id', labelKey: 'sortByJupasId', compactLabelKey: 'sortByJupasIdCompact', defaultDesc: false },
    { columnId: 'programmeName', labelKey: 'sortByProgrammeName', compactLabelKey: 'sortByProgrammeNameCompact', defaultDesc: false },
] as const;

interface ProgrammeSortControlsProps {
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    translate: (key: any) => string;
}

export const ProgrammeSortControls: React.FC<ProgrammeSortControlsProps> = ({
    sorting,
    setSorting,
    translate,
}) => {
    const active = sorting[0];

    const handleSort = (columnId: string, defaultDesc: boolean) => {
        setSorting(prev =>
            prev[0]?.id === columnId
                ? [{ id: columnId, desc: !prev[0].desc }]
                : [{ id: columnId, desc: defaultDesc }]
        );
    };

    return (
        <div className="flex flex-wrap items-center gap-2">
            {PROGRAMME_SORT_OPTIONS.map(option => {
                const isActive = active?.id === option.columnId;
                return (
                    <button
                        key={option.columnId}
                        type="button"
                        onClick={() => handleSort(option.columnId, option.defaultDesc)}
                        title={translate(option.labelKey)}
                        className={`flex items-center gap-1 whitespace-nowrap rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${
                            isActive
                                ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <span className="hidden lg:inline">{translate(option.labelKey)}</span>
                        <span className="lg:hidden">{translate(option.compactLabelKey)}</span>
                        {isActive
                            ? (active.desc ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />)
                            : <ChevronsUpDown className="h-4 w-4 text-gray-400" />}
                    </button>
                );
            })}
        </div>
    );
};
